import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaRobot,
  FaMap,
  FaCalendarCheck,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);
  const features = [
    {
      icon: FaRobot,
      title: "AI-Powered Planning",
      description:
        "Get personalized trip itineraries generated by advanced AI technology tailored to your preferences",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: FaMap,
      title: "Interactive Maps",
      description:
        "Explore destinations with interactive maps and location guides to discover hidden gems",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: FaCalendarCheck,
      title: "Trip Management",
      description:
        "Organize and manage all your trips in one convenient place with easy access",
      color: "bg-purple-100 text-purple-600",
    },
  ];

  const benefits = [
    "Smart AI recommendations",
    "Real-time trip updates",
    "Collaborative planning",
    "Budget tracking",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <section className="py-6 lg:py-32">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl md:text-7xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-6 leading-tight">
                Plan Your Perfect Trip with{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  AI
                </span>
              </h1>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-sm md:text-2xl text-gray-600 dark:text-gray-300 mb-6 lg:mb-12 max-w-3xl mx-auto px-1"
            >
              Discover the world with intelligent travel planning. Get
              personalized itineraries and explore destinations with our
              AI-powered platform.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center px-0 sm:px-0"
            >
              <Link
                to="/register"
                className="group w-full sm:w-auto bg-blue-600 text-white px-4 md:px-8 py-2.5 md:py-4 rounded-lg text-sm md:text-base font-semibold hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-xl flex items-center justify-center min-h-[44px]"
              >
                Get Started Free
                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto border border-blue-600 text-blue-600 dark:text-blue-400 px-4 md:px-8 py-2.5 md:py-4 rounded-lg text-sm md:text-base font-semibold hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 dark:hover:text-white transition-all duration-300 shadow-md hover:shadow-xl flex items-center justify-center min-h-[44px]"
              >
                Sign In
              </Link>
            </motion.div>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="hidden md:flex mt-8 lg:mt-12 flex-wrap justify-center gap-3 lg:gap-6 px-2"
            >
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center text-sm lg:text-base text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-full shadow-sm border border-gray-100 dark:border-gray-700"
                >
                  <FaCheckCircle className="text-green-500 mr-2 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-6 lg:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-6 lg:mb-16"
          >
            <h2 className="text-xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-4">
              Why Choose AI Trip Planner?
            </h2>
            <p className="text-sm lg:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2">
              Experience the future of travel planning with our powerful
              features
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-gray-50 dark:bg-gray-800 p-3 lg:p-8 rounded-xl lg:rounded-2xl hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div
                  className={`w-10 h-10 lg:w-16 lg:h-16 ${feature.color} rounded-xl lg:rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-6 transition-transform duration-300`}
                >
                  <feature.icon className="text-lg lg:text-3xl" />
                </div>
                <h3 className="text-lg lg:text-2xl font-bold mb-1 lg:mb-4 text-gray-900 dark:text-white text-center">
                  {feature.title}
                </h3>
                <p className="text-xs lg:text-base text-gray-600 dark:text-gray-300 leading-relaxed text-center">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-6 lg:py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-5xl font-bold text-white mb-4 lg:mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-sm lg:text-xl text-blue-100 mb-6 px-2">
              Join thousands of travelers who trust AI Trip Planner for their
              adventures
            </p>
            <Link
              to="/register"
              className="inline-block w-full sm:w-auto bg-white text-blue-600 px-4 md:px-8 py-2.5 md:py-4 rounded-lg text-sm md:text-base font-bold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl min-h-[44px]"
            >
              Create Your Free Account
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
